<template>
    <v-dialog
        v-model="showDialog"
        width="560"
        :persistent="loading || (showPromptRemarks.status && !loading)"
    >
        <v-card style="padding: 20px;">
            <v-fade-transition hide-on-leave>
                <div v-show="!isProceedToCancel && !showPromptRemarks.status">
                    <h1>Cancel Portal Approval</h1>
                    <p>You are about to cancel this approval.</p>
                    <v-alert
                        v-show="errors.length === 0"
                        text
                        prominent
                        type="info"
                        color="teal darken-1"
                    >
                        This approval will be cancelled and can no longer be used. Do you want to proceed?
                    </v-alert>
                    <v-alert
                        v-for="(warning, index) in warnings"
                        v-show="warnings.length > 0"
                        :key="index"
                        text
                        prominent
                        type="warning"
                    >
                        {{ warning }}
                    </v-alert>
                    <v-alert
                        v-for="(error, index) in errors"
                        v-show="errors.length > 0"
                        :key="index"
                        text
                        prominent
                        type="error"
                    >
                        {{ error }}
                    </v-alert>
                    <v-card-actions style="padding-right: 0;">
                        <v-spacer />
                        <v-btn
                            width="120"
                            outlined
                            color="teal darken-1"
                            @click="showDialog = false"
                        >
                            cancel
                        </v-btn>
                        <v-btn
                            :disabled="errors.length > 0"
                            width="120"
                            color="teal darken-1"
                            class="white--text"
                            @click="showPromptRemarks.status = true"
                        >
                            proceed
                        </v-btn>
                    </v-card-actions>
                </div>
            </v-fade-transition>
            <v-fade-transition hide-on-leave>
                <v-form
                    v-show="showPromptRemarks.status && !loading"
                    ref="form"
                    v-model="form"
                    @submit.prevent="handleSubmitRemarks"
                >
                    <h1>Cancel Portal Approval</h1>
                    <p>Please provide the reason for cancelling this approval.</p>
                    <v-textarea
                        v-model="showPromptRemarks.value"
                        label="Remarks"
                        outlined
                        color="#009688"
                        :rules="[v=>!!v||'This field is required']"
                    />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            width="120"
                            outlined
                            color="teal darken-1"
                            @click="closeCancelApprovalDialog"
                        >
                            cancel
                        </v-btn>
                        <v-btn
                            :disabled="errors.length > 0"
                            width="120"
                            color="teal darken-1"
                            class="white--text"
                            type="submit"
                        >
                            confirm
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-fade-transition>
            <v-fade-transition hide-on-leave>
                <div v-show="isProceedToCancel && loading">
                    <h1>Cancel Portal Approval</h1>
                    <v-progress-linear
                        style="margin: 70px 0;"
                        color="teal darken-1"
                        indeterminate
                        rounded
                        height="8"
                    />
                </div>
            </v-fade-transition>
            <v-fade-transition hide-on-leave>
                <div v-show="isProceedToCancel && !loading && isApprovalHasResult">
                    <h1> {{ approvalResult && approvalResult.status !== 200 ? "Unable to Cancel Approval" : "Approval Cancelled" }} </h1>
                    <p> {{ approvalResult && approvalResult.status !== 200 ? "The cancellation request cannot be processed at this time." : "The approval has been successfully cancelled." }} </p>
                    <v-alert text prominent :type="approvalResult && approvalResult.status !== 200 ? 'error' : 'success'">
                        <div v-if="approvalResult && approvalResult.status !== 200">
                            <ul v-if="approvalResult && approvalResult.data.reason.length > 1">
                                <li
                                    v-for="(item, key) in approvalResult && approvalResult.data.reason"
                                    :key="key"
                                    style="margin: 5px 0;"
                                >
                                    {{ item }}
                                </li>
                            </ul>
                            <span v-else>{{ approvalResult && approvalResult.data.reason[0] }}</span>
                        </div>
                        <div v-else>
                            <span>The member's approval has been successfully cancelled.</span>
                        </div>
                    </v-alert>
                </div>
            </v-fade-transition>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { moment } from '../../../../utils/date-shim'
export default {
    props: ['show', 'approvalResult', 'approvalData'],
    data: ()=>({
        isApprovalHasResult: false,
        form: true,
        showPromptRemarks: {
            menu: false,
            isSubmitted: true,
            status: false,
            value: null
        },
        loading: false,
        isProceedToCancel: false,
        errors: [],
        warnings: []
    }),
    computed: {
        ...mapGetters(['cancelAcuApprovalData']),
        showDialog: {
            get() {
                return this.show
            },
            set() {
                this.handleCloseDialog()
            }
        }
    },
    watch: {
        approvalData(newVal) {
            if (newVal && newVal.diagnostics && Array.isArray(newVal.diagnostics) && newVal.diagnostics.length) {
                let today = moment().utcOffset('+0800').startOf('day')
                let is_account_expired = moment(newVal.diagnostics[0].AEXP_D).utcOffset('+0800').startOf('day').isBefore(today)
                let on_grace_period = newVal.diagnostics[0].GRACE_D && moment(newVal.diagnostics[0].GRACE_D).utcOffset('+0800').startOf('day').isSameOrAfter(today)
                let errors = []

                if(this.checkIfVerified(newVal.diagnostics)) {
                    errors.push('This approval has already been received and verified by the provider.')
                }
                if (this.checkIfDone(newVal.diagnostics)) {
                    errors.push('There are procedures that are already marked done by the provider.')
                }

                this.errors = errors

                let warnings = []
                if (newVal.diagnostics[0].STTS_C !== 'Active') {
                    warnings.push(`Member Status: ${newVal.diagnostics[0].STTS_C}.`)
                }
                if (on_grace_period && !is_account_expired){
                    warnings.push('This member is on grace period.')
                }
                this.warnings = warnings

                this.aexp_d = this.isValidDate(newVal.diagnostics[0].AEXP_D) ? moment(newVal.diagnostics[0].AEXP_D).utcOffset('+0800').startOf('day') : null
                this.last_d = this.isValidDate(newVal.diagnostics[0].LAST_D) ? moment(newVal.diagnostics[0].LAST_D).utcOffset('+0800').startOf('day') : null
                this.pdun_d = this.isValidDate(newVal.diagnostics[0].PDUN_D) ? moment(newVal.diagnostics[0].PDUN_D).utcOffset('+0800').startOf('day') : null
                this.grace_d = this.isValidDate(newVal.diagnostics[0].GRACE_D) ? moment(newVal.diagnostics[0].GRACE_D).utcOffset('+0800').startOf('day') : null
                this.canc_d = String(newVal.diagnostics[0].STTS_C).toUpperCase().includes('FOR CANCELLATION') && this.isValidDate(newVal.diagnostics[0].STTS_C) ? moment(this.approvalData.diagnostics[0].STTS_D).utcOffset('+0800').startOf('day') : null
            }
        },
        cancelAcuApprovalData() {
            this.loading = false
            this.$refs.form.reset()
        }
    },
    methods: {
        handleSubmitRemarks() {
            if (this.$refs && this.$refs.form.validate()) {
                this.showPromptRemarks.status = false
                this.loading = false
                this.isProceedToCancel = true
                this.isApprovalHasResult = true
                this.$emit('cancelApproval', this.showPromptRemarks.value)
            }
        },
        closeCancelApprovalDialog() {
            this.showPromptRemarks.status = false
            this.showDialog = false
        },
        handlePromptValidatyDays() {
            this.showPromptRemarks.status = true
        },
        handleCloseDialog() {
            this.$refs.form.reset()
            this.$emit('closeDialog')
            setTimeout(()=>{
                this.loading = false
                this.isProceedToCancel = false
                this.isApprovalHasResult = false
            }, 300)
        },
        checkIfVerified (diagnostics) {
            for (let i = 0; i < diagnostics.length; i++) {
                if (String(diagnostics[i].VERIFIED).trim().toUpperCase() === 'YES') {
                    return true
                }
            }
            return false
        },
        checkIfDone(diagnostics) {
            for (let i = 0; i < diagnostics.length; i++) {
                if (String(diagnostics[i].DONE).trim().toUpperCase() === 'YES') {
                    return true
                }
            }
            return false
        },
        isValidDate(datestring) {
            let date = new Date(datestring)
            return !isNaN(date.getTime())
        }
    }
}
</script>

<style scoped>
h1 {
    font-size: 19px;
    text-align: center;
    color: #2C3E50;
    margin-bottom: 10px;
}
p {
    color: #7F8C8D;
    text-align: center
}
</style>
